import { environment } from '../../../../environments/environment';
import { IUser } from '../../classes/IUser';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { INewOrderId } from './new-order-id';
import { Sites } from './order';
import { IUpdateSubmittedOrder } from './update-submitted-order';
import { IUpdateUserOrder } from './update-order';
import { Order } from '../../classes/order';
import { Document } from '../../classes/document';

@Injectable({
    providedIn: 'root'
})
export class OrderService {
    private baseUrl = environment.apiUrls.order;
    private allOrdersSubject$ = new BehaviorSubject<Order[]>([]);
    private totalSegments = 50;

    public allOrdersObserver = this.allOrdersSubject$.asObservable();
    private listLoaded = false;

    constructor(private http: HttpClient) {}

    public getSubmittedOrders(site: string): Observable<Order[]> {
        const url = `${this.baseUrl}/${site}`;

        return new Observable<Order[]>((observer) => {
            let allOrders: Order[] = [];

            let continueFetching = false;
            let nextPages: (string | number)[] | undefined;

            const fetchPage = async () => {
                do {
                    try {
                        const response = await firstValueFrom(
                            this.http.post<{
                                orders: Order[];
                                nextPageKeys?: (string | number)[];
                            }>(url, { exclusiveStartKeys: nextPages })
                        );
                        allOrders = allOrders.concat(response.orders);
                        this.allOrdersSubject$.next(allOrders);

                        nextPages = response.nextPageKeys;

                        // If we have a segment that is not done we continue fetching
                        continueFetching = nextPages.some((v) => v !== -1);
                    } catch (error) {
                        observer.error(error);
                        return;
                    }
                } while (continueFetching && !observer.closed);

                if (!observer.closed) {
                    observer.next(allOrders);
                    observer.complete();
                    this.listLoaded = true;
                }
            };

            fetchPage();

            return () => {
                // This function is called when the observable is unsubscribed
                // We don't need to do anything here as the loop will stop when observer.closed becomes true
            };
        });
    }

    public getListLoaded() {
        return this.listLoaded;
    }

    public getSubmittedOrder(id: string, site: Sites): Observable<Order> {
        const url = `${this.baseUrl}/${id}/site/${site}`;
        return this.http.get<Order>(url);
    }

    public getNewOrderId(site: Sites): Observable<INewOrderId> {
        const url = `${this.baseUrl}/${site}`;
        return this.http.patch<INewOrderId>(url, {});
    }

    public updateSubmittedOrder(order: IUpdateSubmittedOrder) {
        const url = `${this.baseUrl}/submitted`;
        return this.http.put<void>(url, order);
    }

    public getDocumentPreSignedUrl(documentId: string) {
        const params = new HttpParams({
            fromObject: {
                file: encodeURIComponent(documentId)
            }
        });
        const url = `${this.baseUrl}/download`;

        return this.http.get(url, { responseType: 'text', params });
    }

    public updateUserOrder(order: IUpdateUserOrder): Observable<IUser[]> {
        const url = `${this.baseUrl}/user`;
        return this.http.post<IUser[]>(url, order);
    }

    public async downloadDocument(doc: Document): Promise<void> {
        try {
            const preSignedLink = await firstValueFrom(this.getDocumentPreSignedUrl(doc.id));

            const response = await fetch(preSignedLink);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = doc.name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error in document download process:', error);
        }
    }
}
